// services/push.js
// Firebase web push: init, request permission, get FCM token (with localStorage fallback),
// foreground onMessage. Used to obtain the deviceToken for both push and the WebSocket.
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID
}

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY
const FALLBACK_KEY = 'notif_device_id'

let messaging = null

// Stable random id persisted in localStorage, used when FCM is unavailable so the WS still works.
function fallbackDeviceId() {
  let id = localStorage.getItem(FALLBACK_KEY)
  if (!id) {
    id = `web-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)}`
    localStorage.setItem(FALLBACK_KEY, id)
  }
  return id
}

// Initialize Firebase + messaging. Safe to call once; returns false if unsupported/misconfigured.
export async function initPush() {
  try {
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId) return false
    if (!(await isSupported())) return false
    const app = initializeApp(firebaseConfig)
    messaging = getMessaging(app)
    return true
  } catch (e) {
    console.error('[push] initPush failed', e)
    messaging = null
    return false
  }
}

// Returns { token, isFcm }. Falls back to a stable localStorage id when FCM can't provide a token.
export async function getDeviceToken() {
  try {
    if (!messaging) return { token: fallbackDeviceId(), isFcm: false }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { token: fallbackDeviceId(), isFcm: false }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    })

    if (!token) return { token: fallbackDeviceId(), isFcm: false }
    return { token, isFcm: true }
  } catch (e) {
    console.error('[push] getDeviceToken failed, using fallback', e)
    return { token: fallbackDeviceId(), isFcm: false }
  }
}

// Foreground messages (app focused). cb receives the FCM payload.
export function onForegroundMessage(cb) {
  if (!messaging) return
  try {
    onMessage(messaging, cb)
  } catch (e) {
    console.error('[push] onForegroundMessage failed', e)
  }
}
